/**
 * Connection state, said out loud (C-T22 release readiness).
 *
 * Sits above every screen because losing the connection matters whatever the player is looking at.
 * It is deliberately quiet for `connecting`: the boot and lobby screens already have their own
 * copy for that, and a banner during a normal first connect would train people to ignore it.
 */
import { useEffect, useState } from 'react';
import { store, useStore } from '../../store/store';
import { connectTable, getRuntime } from '../runtime';
import { connectionBanner } from '../states';
import { Button } from '../ui';

/**
 * Leave a code that will never work.
 *
 * Also strips it from the address. Without that a reload walks straight back into the same refusal,
 * which is the whole trap a shared-but-expired link sets: the player cannot get out of the link by
 * doing the one thing everyone tries.
 */
function backToLobby(): void {
  try {
    const url = new URL(location.href);
    url.searchParams.delete('join');
    url.searchParams.delete('code');
    history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* history blocked; the state change below still gets them out */
  }
  const st = store.getState();
  st.setSession({ connection: 'idle', joinFailure: null });
  st.setPresentation({ screen: 'lobby' });
}

/** `navigator.onLine`, kept live. Only trustworthy in the negative — see `connectionBanner`. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function ConnectionBanner() {
  const connection = useStore((s) => s.session.connection);
  const joinFailure = useStore((s) => s.session.joinFailure);
  const online = useOnline();
  const [busy, setBusy] = useState(false);

  // `lostReason` lives on the driver rather than the store, and only matters once we are lost.
  const lostReason = connection === 'lost' ? ((getRuntime()?.driver as { lostReason?: string | null } | undefined)?.lostReason ?? null) : null;
  const banner = connectionBanner({ connection, online, lostReason, joinFailure });
  if (!banner) return null;

  return (
    <div className={`conn conn--${banner.tone}`} role="status" aria-live={banner.live} data-testid="connection-banner">
      <span className="conn__dot" aria-hidden />
      <span>{banner.text}</span>
      {banner.action === 'retry' && (
        <Button
          testId="connection-retry"
          disabled={busy || !online}
          onClick={() => {
            setBusy(true);
            void connectTable({}).finally(() => setBusy(false));
          }}
        >
          {busy ? 'Trying…' : 'Try again'}
        </Button>
      )}
      {banner.action === 'lobby' && (
        <Button testId="connection-lobby" onClick={backToLobby}>
          Back to lobby
        </Button>
      )}
    </div>
  );
}
